import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { ArrowRight, CreditCard, Lightning, Sparkle, Shield, Check } from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Preview envs proxy native `fetch()` through a postMessage bridge that
// occasionally can't clone the Request object. Axios uses XHR under the
// hood and bypasses that bridge, so auth/billing flows stay reliable in
// both preview and production. We pin the adapter to 'xhr' explicitly
// because axios 1.7+ can auto-select the fetch adapter in some envs.
const authClient = axios.create({ adapter: "xhr" });
authClient.interceptors.request.use((config) => {
  const token = localStorage.getItem("id_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Two-step onboarding shown to any authenticated user whose backend
// record has `needs_card = true`. Step 1 lets them pick a tier and
// redirects to Stripe Checkout (`/billing/start-trial`). Step 2 runs
// when they return with `?status=success&session_id=…`: we poll
// `/billing/status/{id}` + `/auth/me` until the backend confirms the
// card is attached, then hand control back to the app.
export default function CardOnboarding({ user, onComplete, onLogout }) {
  const urlParams = new URLSearchParams(window.location.search);
  const status = urlParams.get("status");
  const sessionId = urlParams.get("session_id");
  const initialTier = urlParams.get("tier") || "starter";

  const [loadingTier, setLoadingTier] = useState(null);
  const [verifying, setVerifying] = useState(status === "success" && sessionId);

  const clearQuery = useCallback(() => {
    window.history.replaceState({}, "", "/");
  }, []);

  // Verify flow: poll session status + /auth/me until the backend flips
  // needs_card = false. Gives up with a friendly error after ~30s.
  useEffect(() => {
    if (!verifying) return;

    let cancelled = false;
    let attempts = 0;
    const MAX = 15;            // 15 * 2s = 30s timeout

    const tick = async () => {
      if (cancelled) return;
      attempts += 1;
      try {
        // This call processes the tx server-side on first paid status.
        await authClient.get(`${API}/billing/status/${sessionId}`).catch(() => null);

        const res = await authClient.get(`${API}/auth/me`);
        const fresh = res?.data?.user;
        if (fresh && !fresh.needs_card) {
          if (cancelled) return;
          localStorage.setItem("id_user", JSON.stringify(fresh));
          toast.success("Card on file. Trial active.");
          clearQuery();
          onComplete(fresh);
          return;
        }
      } catch {
        /* ignore — retry */
      }
      if (attempts >= MAX) {
        if (cancelled) return;
        setVerifying(false);
        toast.error("Couldn't confirm your card. Please try again.");
        clearQuery();
        return;
      }
      setTimeout(tick, 2000);
    };

    tick();
    return () => { cancelled = true; };
  }, [verifying, sessionId, onComplete, clearQuery]);

  // If cancelled, just clear the query string and let them retry.
  useEffect(() => {
    if (status === "cancelled") {
      toast.info("Checkout cancelled.");
      clearQuery();
    }
  }, [status, clearQuery]);

  const handleSelect = (tier) => {
    // Route into the in-app premium custom checkout instead of bouncing
    // to Stripe's hosted page. Falls back to a hard redirect because
    // CardOnboarding renders OUTSIDE BrowserRouter (App.js mounts it
    // directly when `needs_card && ?status=success`), so `useNavigate`
    // isn't available here.
    setLoadingTier(tier);
    // Meta Pixel: this is the moment the merchant commits to a tier
    // (clicked Starter/Premium). Fire InitiateCheckout — the strongest
    // upper-funnel optimization signal for cold paid traffic.
    try {
      import("../lib/metaPixel").then(({ trackEvent }) => {
        trackEvent("InitiateCheckout", {
          content_name: tier,
          content_category: "subscription",
          currency: "USD",
        });
      });
    } catch {}

    // PERF: the moment the merchant clicks their plan, kick off:
    //   1. The /billing/setup-intent network call (so the result is
    //      already in sessionStorage by the time Checkout.jsx mounts —
    //      saves ~360ms of "Preparing your secure checkout…").
    //   2. A dynamic import of Checkout.jsx so its lazy chunk + the
    //      Stripe.js bundle are warmed in parallel with the SPA nav.
    // Both are fire-and-forget — the navigation itself proceeds
    // immediately so the user sees the new page right away.
    try {
      import("../pages/Checkout");
    } catch {}

    try {
      const token = localStorage.getItem("id_token");
      if (token) {
        // Skip if we already have a fresh prefetched intent for this tier.
        let skipFetch = false;
        try {
          const cachedRaw = sessionStorage.getItem("id_setup_intent");
          if (cachedRaw) {
            const cached = JSON.parse(cachedRaw);
            const ageMs = Date.now() - (cached?.fetched_at || 0);
            if (
              cached?.client_secret &&
              cached?.tier === tier &&
              ageMs < 5 * 60 * 1000
            ) {
              skipFetch = true;
            }
          }
        } catch {}

        if (!skipFetch) {
          authClient
            .post(`${API}/billing/setup-intent`, {
              tier,
              origin_url: window.location.origin,
            })
            .then((res) => {
              try {
                sessionStorage.setItem("id_setup_intent", JSON.stringify({
                  tier,
                  client_secret: res.data?.client_secret,
                  customer_email: res.data?.customer_email,
                  fetched_at: Date.now(),
                }));
              } catch {}
            })
            .catch(() => { /* fall back to in-Checkout fetch */ });
        }
      }
    } catch {}

    try {
      window.location.href = `/checkout/${tier}`;
    } catch (e) {
      toast.error("Couldn't open checkout", { description: e?.message || "Please try again" });
      setLoadingTier(null);
    }
  };

  if (verifying) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center px-4" data-testid="card-onboarding-verifying">
        <div className="max-w-md w-full text-center text-white">
          <div className="w-14 h-14 bg-[#9FFF00] mx-auto mb-6 flex items-center justify-center animate-pulse">
            <CreditCard size={26} weight="bold" className="text-black" />
          </div>
          <h1 className="text-3xl font-black font-heading mb-3 tracking-tight">
            Activating your trial…
          </h1>
          <p className="text-white/70 text-[15px]">
            Confirming your card on file. This takes a few seconds.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="card-onboarding">
      <div className="max-w-4xl mx-auto px-4 py-10">
        <div className="flex items-center justify-between mb-10">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 bg-[#9FFF00] flex items-center justify-center">
              <span className="text-black font-black text-sm">ID</span>
            </div>
            <span className="font-black text-lg font-heading">InfluenceDetector</span>
          </div>
          <button
            onClick={onLogout}
            className="text-sm text-white/60 hover:text-white transition-colors"
            data-testid="card-onboarding-logout"
          >
            Log out
          </button>
        </div>

        <div className="mb-10 max-w-2xl">
          <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#9FFF00] mb-3">
            Step 2 of 2 · Start your 7-day trial
          </p>
          <h1 className="text-4xl sm:text-5xl font-black leading-[0.95] tracking-tight font-heading mb-4">
            Add a card to unlock{" "}
            <span className="text-[#9FFF00]">7 days free.</span>
          </h1>
          <p className="text-white/70 text-base leading-relaxed">
            Welcome {user?.company_name || user?.email}. Pick the plan you'd like
            once your trial ends. We won't charge the trial fee today — cancel
            any time before day 7 and you pay nothing.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
          <PlanCard
            planKey="starter"
            name="Starter"
            price={29.99}
            icon={Lightning}
            tagline="Keep the basics after your trial."
            features={[
              "Order + customer detection",
              "Instagram, TikTok, YouTube scanning",
              "AI analysis per influencer",
              "Email notifications",
              "Unlimited live orders",
            ]}
            accent={false}
            loading={loadingTier === "starter"}
            disabled={loadingTier !== null}
            onSelect={() => handleSelect("starter")}
            initialTier={initialTier}
          />
          <PlanCard
            planKey="premium"
            name="Premium"
            price={79.99}
            icon={Sparkle}
            tagline="Everything — built for serious brands."
            features={[
              "Everything in Starter",
              "Subscriber Influencers",
              "Social Media Followers",
              "Watchlist — track creators 24/7",
              "500 historical-order credits/mo",
            ]}
            accent={true}
            loading={loadingTier === "premium"}
            disabled={loadingTier !== null}
            onSelect={() => handleSelect("premium")}
            initialTier={initialTier}
          />
        </div>

        <div className="flex items-start gap-3 p-4 bg-white/5 border border-white/10">
          <Shield size={20} weight="bold" className="text-[#9FFF00] flex-shrink-0 mt-0.5" />
          <p className="text-sm text-white/70 leading-relaxed">
            <strong className="text-white">Secure checkout with Stripe.</strong>{" "}
            We never see or store your card — only a tokenized reference. Cancel
            anytime from Settings. No charge if you cancel within the 7-day trial.
          </p>
        </div>
      </div>
    </div>
  );
}

function PlanCard({ planKey, name, price, icon: Icon, tagline, features, accent, loading, disabled, onSelect, initialTier }) {
  const highlight = initialTier === planKey;
  return (
    <div
      className={`relative p-6 border-2 ${
        accent
          ? "bg-[#0A0A0A] border-[#9FFF00]"
          : "bg-white text-[#0A0A0A] border-white/15"
      }`}
      data-testid={`card-onboarding-tier-${planKey}`}
    >
      {accent && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#9FFF00] text-black text-[10px] font-bold uppercase tracking-[0.2em] px-3 py-1">
          Most popular
        </div>
      )}
      {highlight && !accent && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#9FFF00] text-black text-[10px] font-bold uppercase tracking-[0.2em] px-3 py-1">
          You picked this
        </div>
      )}

      <div className={`w-10 h-10 flex items-center justify-center mb-4 ${
        accent ? "bg-[#9FFF00]/10 border border-[#9FFF00]/30" : "bg-[#F5F5F5] border border-black/10"
      }`}>
        <Icon size={18} weight="bold" className={accent ? "text-[#9FFF00]" : "text-[#0A0A0A]"} />
      </div>

      <h2 className="text-2xl font-black tracking-tight font-heading">{name}</h2>
      <p className={`text-sm mb-4 ${accent ? "text-white/60" : "text-[#525252]"}`}>
        {tagline}
      </p>

      <div className="mb-5">
        <div className="flex items-baseline gap-1">
          <span className="text-4xl font-black tabular-nums font-heading">${price}</span>
          <span className={`text-sm ${accent ? "text-white/50" : "text-[#737373]"}`}>
            /month after trial
          </span>
        </div>
      </div>

      <Button
        onClick={onSelect}
        disabled={disabled}
        data-testid={`card-onboarding-select-${planKey}`}
        className={`w-full rounded-none font-bold py-3 text-sm uppercase tracking-wider ${
          accent
            ? "bg-[#9FFF00] hover:bg-[#8AE600] text-black"
            : "bg-[#0A0A0A] hover:bg-[#1F1F1F] text-white"
        }`}
      >
        {loading ? "Redirecting…" : `Start ${name} trial`}
        {!loading && <ArrowRight size={16} className="ml-2" />}
      </Button>

      <ul className={`mt-5 pt-5 border-t space-y-2 ${accent ? "border-white/10" : "border-black/10"}`}>
        {features.map((f) => (
          <li key={f} className="flex items-start gap-2 text-sm">
            <Check size={15} weight="bold" className={`flex-shrink-0 mt-0.5 ${
              accent ? "text-[#9FFF00]" : "text-[#4A7A00]"
            }`} />
            <span className={accent ? "text-white/90" : "text-[#262626]"}>{f}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
